import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FormattedNumber } from "react-intl";
import moment from "moment";
import ICatalog_Item from "@models/ICatalog_Item";
import { useAppContextNonNull } from "@contexts/AppContext";
import { IIncomingPurchaseOrderDetail } from "@models/DTO/IOnGetIncomingPurchaseOrdersDTO";
import InventoryService from "@services/InventoryService";
import UIService from "@services/UIService";
import OwButton from "@components/shared/OwButton/OwButton";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import "./InventoryItemIncomingQtyDetailSidebar.scss";

type Props = {
    catalogItem: ICatalog_Item;
    closeFn: () => void;
};

const DEFAULT_LIMIT = 25;

export const InventoryItemIncomingQtyDetailSidebar = (props: Props) => {
    const { catalogItem, closeFn } = props;
    const { userModel, supplierModel } = useAppContextNonNull();

    const [purchaseOrders, setPurchaseOrders] = useState<IIncomingPurchaseOrderDetail[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalIncomingQty, setTotalIncomingQty] = useState(0);
    const [catalogItemSku, setCatalogItemSku] = useState<string | undefined>();
    const [catalogItemName, setCatalogItemName] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    const fetchIncomingPurchaseOrders = useCallback(async () => {
        setIsLoading(true);

        try {
            const result = await InventoryService.getIncomingPurchaseOrders(
                {
                    catalogItemGuid: catalogItem.guid,
                    limit,
                    offset,
                },
                userModel,
                supplierModel
            );

            if (result.success && result.data) {
                setPurchaseOrders(result.data.purchaseOrders);
                setTotalCount(result.data.totalCount);
                setTotalIncomingQty(result.data.totalIncomingQty);
                setCatalogItemSku(result.data.catalogItemSku);
                setCatalogItemName(result.data.catalogItemName);
            }
        } catch (error) {
            console.error("Error fetching incoming purchase orders:", error);
        } finally {
            setIsLoading(false);
        }
    }, [catalogItem.guid, limit, offset, userModel, supplierModel]);

    const handleExportCsv = async () => {
        setIsExporting(true);

        try {
            const result = await InventoryService.getIncomingPurchaseOrdersCsv(catalogItem.guid, userModel, supplierModel);

            if (result.success && result.csvData && result.filename) {
                UIService.downloadCSVData(result.csvData, result.filename);
            }
        } catch (error) {
            console.error("Error exporting incoming purchase orders:", error);
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        fetchIncomingPurchaseOrders();
    }, [fetchIncomingPurchaseOrders]);

    const handlePaginationChanged = (newOffset: number, newLimit: number) => {
        setOffset(newOffset);
        setLimit(newLimit);
    };

    const formatDate = (dateString: string) => {
        return moment(dateString).format("MM/DD/YYYY");
    };

    return (
        <div className="InventoryItemIncomingQtyDetailSidebar">
            <div className="sidebar-header">
                <div className="header-content">
                    <h5 className="sidebar-title">Incoming Qty Details</h5>
                    <div className="item-info">
                        <strong>{catalogItemSku || catalogItem.sku}</strong>
                        {(catalogItemName || catalogItem.description) && <span> - {catalogItemName || catalogItem.description}</span>}
                    </div>
                    <div className="summary-badge">
                        Total Incoming: <FormattedNumber value={totalIncomingQty} minimumFractionDigits={0} />
                    </div>
                </div>
                <OwButton centerIconClass="fas fa-times" className="btn-danger" fn={closeFn} />
            </div>

            <div className="actions-container">
                <OwButton
                    fn={handleExportCsv}
                    disabled={isExporting || isLoading || totalCount === 0}
                    title={isExporting ? "Exporting..." : "Export CSV"}
                    leftIconClass="fas fa-file-csv"
                    className="btn-primary btn-sm"
                    skipTranslate
                />
            </div>

            <div className="table-container">
                {isLoading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                    </div>
                ) : purchaseOrders.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-inbox fa-3x mb-3"></i>
                        <p>No open purchase orders found for this item.</p>
                    </div>
                ) : (
                    <table className="orders-table">
                        <thead>
                            <tr>
                                <th>PO #</th>
                                <th>Date</th>
                                <th>Vendor</th>
                                <th className="text-end">Ordered</th>
                                <th className="text-end">Received</th>
                                <th className="text-end">Outstanding</th>
                            </tr>
                        </thead>
                        <tbody>
                            {purchaseOrders.map((po, index) => (
                                <tr key={`${po.poGuid}-${index}`}>
                                    <td>
                                        <Link to={`/purchase_orders/${po.poGuid}`} className="text-primary" onClick={closeFn}>
                                            {po.poNo}
                                        </Link>
                                    </td>
                                    <td>{po.poDate ? formatDate(po.poDate) : "--"}</td>
                                    <td>{po.vendorSourceName}</td>
                                    <td className="text-end">
                                        <FormattedNumber value={po.qtyOrdered} minimumFractionDigits={0} />
                                    </td>
                                    <td className="text-end">
                                        <FormattedNumber value={po.qtyReceived} minimumFractionDigits={0} />
                                    </td>
                                    <td className="text-end">
                                        <FormattedNumber value={po.qtyOutstanding} minimumFractionDigits={0} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {totalCount > 0 && (
                <div className="pagination-container">
                    <PaginationBar
                        totalRecords={totalCount}
                        limit={limit}
                        offset={offset}
                        onPaginationChanged={handlePaginationChanged}
                        isActive={isLoading}
                        hideLimit
                        centered
                    />
                </div>
            )}
        </div>
    );
};
